/**
 * Arena **XP gems** - the VS-style ground drops that replaced kill-time XP
 * (PRD: XP orbs). A slain enemy no longer credits `state.xp` directly from the
 * reaper; it drops one gem entity at its corpse tile carrying its tier's base
 * XP, and the runner earns that XP by walking within the magnet radius - the
 * classic Vampire Survivors loop (kiting *through* your kills is the reward
 * beat). A gem is deliberately dumb on the sim side: a stationary
 * `position` + {@link XpGemComponent} entity with no `mobile`, no ttl, and no
 * server-side homing - the satisfying attract/suck-in is **pure client
 * animation** played off the gem's removal delta (the projectile
 * impact-on-removal pattern, PRD #399 Slice D), so the wire carries one spawn
 * and one removal per gem and the 10 Hz snapshot cadence never stutters the
 * magnetize.
 *
 * Pile growth is bounded by {@link ARENA_XP_GEM_CAP}: past it, the **oldest**
 * gem on the field folds its value into the freshly dropped one (VS's
 * gem-merge), so XP is conserved by construction while the entity count -
 * and the wire - stay flat under a 68-mob bestiary's kill rate.
 *
 * Pure and clock-free like every arena module: no wall clock, no randomness
 * (spawn position is the corpse tile; the client scatters the *visual* orbs
 * deterministically from the entity id).
 */
import type { Entity, EntityId, PositionComponent } from './entity';
import type { World } from './world';
import { Direction } from './direction';
import { ARENA_MAGNET_TILES_PER_LEVEL } from './arenaWeapons';

/**
 * How close (Euclidean, tiles) the runner must come for a ground gem to be
 * collected - the "magnet" radius. 2.5 tiles collects a comfortable disc
 * around the runner without hoovering the whole screen: you still steer
 * *through* your drops, which is the VS pickup loop. The client mirrors this
 * (in screen px) to decide whether a removed gem plays the suck-in or a quiet
 * fade (a cap-merge of a distant gem must not fly across the field).
 */
export const XP_GEM_MAGNET_RADIUS_TILES = 2.5;

/**
 * The runner's **effective** magnet radius (tiles) for a `magnet` stat level
 * (PRD #431 Slice A): the base disc grown by
 * {@link ./arenaWeapons.ARENA_MAGNET_TILES_PER_LEVEL} per drafted level (2.5 at
 * level 0 → 6.25 at max level 5). Applied at *pickup* (the `xp-gain`
 * precedent - {@link stepArena}'s step 3.4 reads the runner's live stat), so a
 * mid-run magnet pick immediately sweeps gems already on the ground. The client
 * mirrors this (in screen px) off its own accepted magnet picks to classify a
 * removal as suck-in vs cap-merge fade.
 */
export function arenaMagnetRadius(magnetStatLevel: number): number {
  return XP_GEM_MAGNET_RADIUS_TILES + ARENA_MAGNET_TILES_PER_LEVEL * magnetStatLevel;
}

/**
 * Most gems on the field at once. Past the cap the oldest gem merges into the
 * newest ({@link spawnXpGem}), so an untouched battlefield converges on fewer,
 * fatter gems instead of an unbounded entity list. 120 comfortably out-paces
 * the 150-enemy cap's kill rate against a 2.5-tile magnet in practice.
 */
export const ARENA_XP_GEM_CAP = 120;

/** Where a slain enemy drops its gem (its corpse tile; `z` defaults to ground). */
export interface XpGemDropSpot {
  x: number;
  y: number;
  z?: number;
}

/**
 * Drop one XP gem at `at`, worth `value` base XP, and register it in `gems`
 * (the run's oldest-first ledger). At {@link ARENA_XP_GEM_CAP} the oldest gem
 * is despawned and its value folded into the new one - XP conserved, entity
 * count bounded. A non-positive `value` drops nothing (a zero-XP kill has no
 * gem to give).
 */
export function spawnXpGem(
  world: World,
  gems: EntityId[],
  at: XpGemDropSpot,
  value: number,
): Entity | null {
  if (value <= 0) return null;
  let merged = 0;
  if (gems.length >= ARENA_XP_GEM_CAP) {
    const oldestId = gems.shift()!;
    const oldest = world.get(oldestId);
    merged = oldest?.xpGem?.value ?? 0;
    world.remove(oldestId);
  }
  const position: PositionComponent = { x: at.x, y: at.y, facing: Direction.S };
  if (at.z) position.z = at.z;
  const gem = world.createEntity({ position, xpGem: { value: value + merged } });
  gems.push(gem.id);
  return gem;
}

/** One collected gem: the id the client sees removed, and its base XP value. */
export interface CollectedGem {
  id: EntityId;
  value: number;
}

/**
 * Collect every gem within `radius` tiles (Euclidean) of the runner: each is
 * removed from the world and from `gems` (in place), and returned so the caller
 * credits the XP (through the `xp-gain` stat - applied at pickup, never at
 * drop). Walking into a pile collects the whole disc in one tick, so the
 * client's suck-in animates them **as a group** - the dopamine beat.
 *
 * `radius` is the runner's *effective* magnet radius, resolved at the call site
 * from the `magnet` stat ({@link arenaMagnetRadius}, PRD #431 Slice A) - so a
 * mid-run magnet pick sweeps gems already on the ground. A **Vacuum** pickup
 * passes `Infinity` to hoover the whole field in one tick (PRD #431 Slice B) -
 * one parameterization of this same function, no separate collect path.
 */
export function collectXpGems(
  world: World,
  gems: EntityId[],
  runner: { x: number; y: number },
  radius: number,
): CollectedGem[] {
  const collected: CollectedGem[] = [];
  const radiusSq = radius * radius;
  for (let i = gems.length - 1; i >= 0; i--) {
    const id = gems[i]!;
    const gem = world.get(id);
    if (!gem?.xpGem || !gem.position) {
      // Despawned out from under us (defensive): drop the stale ledger entry.
      gems.splice(i, 1);
      continue;
    }
    const dx = gem.position.x - runner.x;
    const dy = gem.position.y - runner.y;
    if (dx * dx + dy * dy > radiusSq) continue;
    collected.push({ id, value: gem.xpGem.value });
    world.remove(id);
    gems.splice(i, 1);
  }
  return collected;
}

// ---------------------------------------------------------------------------
// The Vacuum pickup (PRD #431 Slice B) - the gem's sibling. A rare elite/boss
// drop; claiming it collects the WHOLE gem field in one tick (the jackpot). It
// is a stationary `position` + `pickup` entity with no `mobile`/`health`, never
// expires (the gem rule), and the whole-field flight is a pure client
// presentation off the same-tick removals (the ADR-0215 invariant).
// ---------------------------------------------------------------------------

/** Chance a downed **elite** drops a Vacuum (a boss always does). Seeded off
 * the run world's rng at the reap, so drops are deterministic and replayable. */
export const ARENA_VACUUM_ELITE_CHANCE = 0.5;

/** Most Vacuums on the field at once. A drop roll is skipped while one lies
 * uncollected - scarcity is the point; the jackpot must stay rare. */
export const ARENA_VACUUM_FIELD_CAP = 1;

/**
 * Drop one Vacuum pickup at `at` and register it in `vacuums` (the run's
 * ledger). Unlike a gem there is no value and no cap-merge: scarcity is
 * enforced by the caller skipping the roll at {@link ARENA_VACUUM_FIELD_CAP}
 * (a second roll while one lies uncollected is dropped). Stationary, no
 * `mobile`/`health`, never expires.
 */
export function spawnVacuum(world: World, vacuums: EntityId[], at: XpGemDropSpot): Entity {
  const position: PositionComponent = { x: at.x, y: at.y, facing: Direction.S };
  if (at.z) position.z = at.z;
  const pickup = world.createEntity({ position, pickup: { kind: 'vacuum' } });
  vacuums.push(pickup.id);
  return pickup;
}

/**
 * Claim every Vacuum within {@link XP_GEM_MAGNET_RADIUS_TILES} of the runner -
 * the base disc, **unaffected by the magnet stat**: you must actually walk onto
 * the vacuum to claim it (the reward for the kite, not a passive sweep). Each
 * claimed vacuum is removed from the world and from `vacuums` (in place); the
 * ids are returned so the caller stages the whole-field gem collect. A stale
 * ledger entry (despawned out from under us) is dropped defensively.
 */
export function collectVacuums(
  world: World,
  vacuums: EntityId[],
  runner: { x: number; y: number },
): EntityId[] {
  const claimed: EntityId[] = [];
  const radiusSq = XP_GEM_MAGNET_RADIUS_TILES * XP_GEM_MAGNET_RADIUS_TILES;
  for (let i = vacuums.length - 1; i >= 0; i--) {
    const id = vacuums[i]!;
    const entity = world.get(id);
    if (!entity?.pickup || !entity.position) {
      vacuums.splice(i, 1);
      continue;
    }
    const dx = entity.position.x - runner.x;
    const dy = entity.position.y - runner.y;
    if (dx * dx + dy * dy > radiusSq) continue;
    claimed.push(id);
    world.remove(id);
    vacuums.splice(i, 1);
  }
  return claimed;
}
